export type PersonType = "juridica" | "natural";
export type DocumentType = "NIT" | "CC" | "CE" | "PASAPORTE";
export type RepresentativeDocumentType = "CC" | "CE" | "PASAPORTE";
export type CertificateType = "factura_electronica" | "firma_digital" | "ssl" | "autenticacion";
export type CertificateClass = "II" | "III";
export type ValidityYears = 1 | 2 | 3;
export type RequestStatus = "pending" | "verified" | "issued" | "rejected" | "revoked" | "expired";

export type FileArtifactType =
  | "rut"
  | "camara_comercio"
  | "cedula"
  | "poder"
  | "formulario"
  | "attachment"
  | "response_document";
export type InlineArtifactType =
  | "csr"
  | "private_key"
  | "pem_certificate"
  | "pfx"
  | "pfx_password"
  | "p12"
  | "p12_password";
export type ArtifactType = FileArtifactType | InlineArtifactType;
export type ArtifactVisibility = "internal" | "public";

export const ENCRYPTED_ARTIFACT_TYPES: InlineArtifactType[] = [
  "private_key",
  "pfx",
  "pfx_password",
  "p12",
  "p12_password",
];

export const INLINE_ARTIFACT_TYPES: InlineArtifactType[] = [
  "csr",
  "private_key",
  "pem_certificate",
  "pfx",
  "pfx_password",
  "p12",
  "p12_password",
];

export const FILE_ARTIFACT_TYPES: FileArtifactType[] = [
  "rut",
  "camara_comercio",
  "cedula",
  "poder",
  "formulario",
  "attachment",
  "response_document",
];

export const R2_URL_PREFIX = "r2://";

export function isR2Backed(fileUrl: string | null | undefined): boolean {
  return !!fileUrl && fileUrl.startsWith(R2_URL_PREFIX);
}

export const REQUEST_TRANSITIONS: Record<RequestStatus, RequestStatus[]> = {
  pending: ["verified", "rejected"],
  verified: ["issued", "rejected"],
  issued: ["revoked", "expired"],
  rejected: [],
  revoked: [],
  expired: [],
};

export interface Subscriber {
  id: string;
  type: PersonType;
  name: string;
  firstName: string | null;
  secondName: string | null;
  firstSurname: string | null;
  secondSurname: string | null;
  documentType: DocumentType;
  documentNumber: string;
  verificationDigit: string | null;
  email: string;
  phone: string | null;
  address: string;
  city: string;
  state: string;
  country: string;
  department: string | null;
  status: "active" | "archived";
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSubscriberInput {
  type: PersonType;
  name?: string;
  firstName?: string;
  secondName?: string | null;
  firstSurname?: string;
  secondSurname?: string | null;
  documentType: DocumentType;
  documentNumber: string;
  verificationDigit?: string;
  email: string;
  phone?: string;
  address: string;
  city: string;
  state: string;
  country?: string;
  department?: string;
}

export interface UpdateSubscriberInput {
  name?: string;
  firstName?: string | null;
  secondName?: string | null;
  firstSurname?: string | null;
  secondSurname?: string | null;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  department?: string;
  /** "archived" archives (reversible), "active" restores. PC-38: no delete. */
  status?: "active" | "archived";
}

export interface ListSubscribersParams {
  page?: number;
  pageSize?: number;
  search?: string;
  includeArchived?: boolean;
}

export interface PaginatedSubscribers {
  data: Subscriber[];
  total: number;
  page: number;
  pageSize: number;
}

export interface Representative {
  id: string;
  subscriberId: string;
  fullName: string;
  firstName: string | null;
  secondName: string | null;
  firstSurname: string | null;
  secondSurname: string | null;
  documentType: RepresentativeDocumentType;
  documentNumber: string;
  email: string | null;
  position: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRepresentativeInput {
  firstName: string;
  secondName?: string | null;
  firstSurname: string;
  secondSurname?: string | null;
  documentType: RepresentativeDocumentType;
  documentNumber: string;
  email?: string;
  position: string;
}

export interface UpdateRepresentativeInput {
  firstName?: string | null;
  secondName?: string | null;
  firstSurname?: string | null;
  secondSurname?: string | null;
  email?: string;
  position?: string;
}

export interface CertificateRequest {
  id: string;
  subscriberId: string;
  type: CertificateType;
  class: CertificateClass;
  validityYears: ValidityYears;
  /** Source of truth since PC-35 (3/6/12/24; legacy rows may carry 36). */
  validityMonths: number;
  status: RequestStatus;
  verifiedBy: string | null;
  verifiedAt: string | null;
  issuedAt: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
  certificateSerial: string | null;
  validFrom: string | null;
  validUntil: string | null;
  paymentLinkId: string | null;
  paidAt: string | null;
  paidAmountCents: number | null;
  paidCurrency: string | null;
  invoice?: {
    number: string;
    amountCents: number;
    currency: string;
    issuedAt: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCertificateRequestInput {
  type: CertificateType;
  class: CertificateClass;
  validityYears: ValidityYears;
}

export interface TransitionCertificateRequestInput {
  status: Exclude<RequestStatus, "pending">;
  verifiedBy?: string;
  revocationReason?: string;
  certificateSerial?: string;
}

export interface Artifact {
  id: string;
  subscriberId: string;
  representativeId: string | null;
  certificateRequestId: string | null;
  type: ArtifactType;
  visibility: ArtifactVisibility;
  fileName: string | null;
  fileUrl: string | null;
  encrypted: boolean;
  createdAt: string;
}

export interface ArtifactWithContent extends Artifact {
  content: string;
}

export type ArtifactValidationStatus = "pass" | "warn" | "fail";

export interface ArtifactValidationCheck {
  id: string;
  label: string;
  status: ArtifactValidationStatus;
  detail?: string;
}

/** AI document-validation verdict for an artifact (PC-29). */
export interface ArtifactValidation {
  artifactId: string;
  overall: "pass" | "warn" | "fail" | "error";
  detectedType: string | null;
  legible: boolean | null;
  checks: ArtifactValidationCheck[];
  extracted: Record<string, unknown> | null;
  model: string | null;
  createdAt: string;
}

export interface CreateFileArtifactInput {
  type: FileArtifactType;
  fileName: string;
  fileUrl: string;
  visibility?: ArtifactVisibility;
  representativeId?: string;
  certificateRequestId?: string;
}

export interface UploadArtifactInput {
  type: FileArtifactType;
  file: File;
  fileName?: string;
  visibility?: ArtifactVisibility;
  representativeId?: string;
  certificateRequestId?: string;
}

export interface CreateInlineArtifactInput {
  type: InlineArtifactType;
  content: string;
  visibility?: ArtifactVisibility;
  representativeId?: string;
  certificateRequestId?: string;
}

export interface GenerateCsrForSubscriberResponse {
  csr: string;
  key: string;
  subject: string;
  algorithm: string;
  keySize: number;
  createdAt: string;
}

export interface GeneratePfxForSubscriberResponse {
  pfx: string;
  pfxContentType: string;
  pfxEncoding: string;
  pfxFileName: string;
  pfxPassword: string;
  friendlyName: string;
  issuer: string;
  subject: string;
  serialNumber: string;
  validFrom: string;
  validTo: string;
  createdAt: string;
}

export interface GenerateP12ForSubscriberInput {
  /** Optional keystore alias. Defaults server-side to `pahcertificar-{documentNumber}`. */
  alias?: string;
}

export interface GenerateP12ForSubscriberResponse {
  p12: string;
  p12ContentType: string;
  p12Encoding: string;
  p12FileName: string;
  p12Password: string;
  alias: string;
  friendlyName: string;
  issuer: string;
  subject: string;
  serialNumber: string;
  validFrom: string;
  validTo: string;
  createdAt: string;
}

export interface UtilityCsrInput {
  pais: string;
  departamento: string;
  ciudad: string;
  empresa: string;
  area: string;
  nombreComun: string;
}

export interface UtilityCsrResponse {
  csr: string;
  key: string;
  subject: string;
  algorithm: string;
  keySize: number;
  createdAt: string;
}

export interface UtilityPfxInput {
  key: string;
  pem: string;
  password: string;
  chain?: string[];
}

export interface UtilityPfxResponse {
  pfx: string;
  pfxContentType: string;
  pfxEncoding: string;
  pfxFileName: string;
  friendlyName: string;
  issuer: string;
  subject: string;
  serialNumber: string;
  validFrom: string;
  validTo: string;
  createdAt: string;
}

export interface UtilityP12Input {
  key: string;
  pem: string;
  password: string;
  /** Optional keystore alias. Defaults to the certificate's CN. */
  alias?: string;
  chain?: string[];
}

export interface UtilityP12Response {
  p12: string;
  p12ContentType: string;
  p12Encoding: string;
  p12FileName: string;
  alias: string;
  friendlyName: string;
  issuer: string;
  subject: string;
  serialNumber: string;
  validFrom: string;
  validTo: string;
  createdAt: string;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export interface HealthResponse {
  status: "ok";
  timestamp: string;
}

export interface AcquirerLookupResponse {
  found: boolean;
  name: string | null;
  email: string | null;
  message: string | null;
  statusCode: string | null;
  documentType: DocumentType | null;
  acquirerType: string;
  documentNumber: string;
}

// ── Andes emission (proxied through our API: POST /api/subscribers/{id}/emit-andes) ─
export interface AndesScreenshot {
  name: string;
  pngBase64: string;
}

export interface AndesEmitInput {
  dryRun?: boolean;
}

export interface AndesPullPemInput {
  mailboxMessageId?: string;
  dryRun?: boolean;
}

export interface AndesEmitResponseData {
  submitted: boolean;
  documentNumber: string;
  subscriberId: string;
  subscriberName: string;
  soporteZipBytes: number;
  soporteFiles: string[];
  message: string;
  screenshots?: AndesScreenshot[];
  attemptId?: string;
  confirmTitle?: string;
  confirmBody?: string;
}

export interface AndesEmitResponse {
  ok: boolean;
  data: AndesEmitResponseData;
  timestamp: string;
}

export interface AndesPullPemResponseData {
  skipped: boolean;
  downloaded: boolean;
  generated: boolean;
  dryRun: boolean;
  message: string;
  pullId?: string;
  turno?: string | null;
  serial?: string | null;
  issuedRequestId?: string | null;
  screenshots?: AndesScreenshot[];
}

export interface AndesPullPemResponse {
  ok: boolean;
  data: AndesPullPemResponseData;
  timestamp: string;
}

// ── Andes request queue (GET /api/andes/requests) ───────────────────────
export interface AndesCertificateRequest {
  solicitudWeb: string;
  fechaSolicitud: string;
  tipoCertificado: string;
  solicitante: string;
  documentoSolicitante: string;
  empresa: string;
  nitEmpresa: string;
  estado: string;
  entidadConvenio: string;
  nitEntidad: string;
  coordinadorExterno: string;
  observaciones: string;
}

export interface AndesRequestsResponseData {
  dateFrom: string;
  dateTo: string;
  totalRecords: number;
  requests: AndesCertificateRequest[];
}

export interface AndesRequestsResponse {
  ok: boolean;
  data: AndesRequestsResponseData;
  timestamp: string;
}

export type AndesEmitOutcome = "dry_run" | "submitted" | "error";

export interface AndesEmitAttemptEvent {
  ts: string;
  level: string;
  msg: string;
  data?: Record<string, unknown>;
}

export interface AndesEmitAttemptShot {
  name: string;
  r2Key: string;
  byteSize: number;
}

export interface AndesEmitRequestPayload {
  personType?: "natural" | "juridica";
  documentNumber?: string;
  subscriberId?: string;
  subscriberName?: string;
  dryRun?: boolean;
  certTypeLabel?: string;
  gridRow?: Record<string, unknown>;
  entidad?: Record<string, unknown> | null;
  vigencia?: Record<string, unknown>;
  csrPem?: string | null;
  soporteFiles?: string[];
  soporteZipBytes?: number;
}

export interface AndesEmitAttempt {
  id: string;
  subscriberId: string;
  outcome: AndesEmitOutcome;
  dryRun: boolean;
  submitted: boolean;
  errorCode: string | null;
  errorMessage: string | null;
  confirmTitle: string | null;
  confirmBody: string | null;
  bridgerMessage: string | null;
  soporteFiles: string[];
  soporteZipBytes: number | null;
  requestPayload?: AndesEmitRequestPayload | null;
  packageFileName?: string | null;
  packageByteSize?: number | null;
  events?: AndesEmitAttemptEvent[];
  shotCount: number;
  shots?: AndesEmitAttemptShot[];
  createdAt: string;
}

export interface AndesEmitAttemptListResponse {
  data: AndesEmitAttempt[];
}

// ── Mailbox (per-subscriber shared box; PC-19) ──────────────────────────────
export type MailboxDirection = "inbound" | "outbound";

export interface MailboxMessage {
  id: string;
  subscriberId: string | null;
  direction: MailboxDirection;
  messageId: string | null;
  inReplyTo: string | null;
  fromAddress: string;
  toAddress: string;
  subject: string | null;
  snippet: string | null;
  hasAttachments: boolean;
  rawR2Key: string;
  seen: boolean;
  receivedAt: string;
  createdAt: string;
}

export interface MailboxListResponse {
  data: MailboxMessage[];
}

export interface MailboxAttachmentMeta {
  index: number;
  filename: string;
  mimeType: string;
  size: number;
}

export interface MailboxMessageDetail {
  message: MailboxMessage;
  text: string | null;
  html: string | null;
  attachments: MailboxAttachmentMeta[];
}

export interface SendFromBoxInput {
  to: string;
  subject: string;
  html?: string;
  text?: string;
  replyTo?: string;
}

// ── Admin: all authenticated users (PC-26) ──────────────────────────────────

export interface AdminUser {
  userId: string;
  phone: string;
  documentType: string;
  documentNumber: string;
  fullName: string;
  email: string;
  emailVerifiedAt: string | null;
  createdAt: string;
  subscriber: {
    id: string;
    type: string;
    name: string;
    city: string | null;
    state: string | null;
  } | null;
  certificates: {
    total: number;
    inProgress: number;
    completed: number;
    paid: number;
  };
  payments: {
    attempts: number;
    succeeded: number;
    totalPaidCents: number;
    lastActivityAt: string | null;
  };
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
}
